import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Les deux mots de passe ne correspondent pas.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Mot de passe réinitialisé.");
      setTimeout(() => nav("/auth?mode=login"), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lien invalide ou expiré.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-100px)] flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        {!token ? (
          <div className="text-center">
            <h1 className="font-heading text-3xl tracking-tight mb-3">Lien invalide</h1>
            <p className="text-[#5C5754] mb-8">
              Ce lien de réinitialisation est incomplet. Merci d'en demander un nouveau.
            </p>
            <Link to="/forgot-password" className="text-[#C86B5E] hover:text-[#B35A4E]">
              Demander un nouveau lien
            </Link>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <div className="w-14 h-14 rounded-full bg-[#7A8B76]/15 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-7 h-7 text-[#7A8B76]" strokeWidth={1.5} />
            </div>
            <h1 className="font-heading text-3xl tracking-tight mb-3">C'est fait !</h1>
            <p className="text-[#5C5754] mb-8">
              Ton mot de passe a été mis à jour. Redirection vers la connexion…
            </p>
            <Link to="/auth?mode=login" className="text-[#C86B5E] hover:text-[#B35A4E]">
              Se connecter maintenant
            </Link>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-full bg-[#C86B5E]/10 flex items-center justify-center mb-6">
              <KeyRound className="w-5 h-5 text-[#C86B5E]" strokeWidth={1.5} />
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mb-3">
              Nouveau mot de passe
            </h1>
            <p className="text-[#5C5754] leading-relaxed mb-8">
              Choisis un nouveau mot de passe (au moins 6 caractères).
            </p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#5C5754] font-semibold">
                  Nouveau mot de passe
                </label>
                <input
                  data-testid="reset-password-input"
                  required
                  type="password"
                  minLength={6}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 w-full bg-white border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-3.5 text-[#2C2A29] transition-colors"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="text-xs tracking-[0.2em] uppercase text-[#5C5754] font-semibold">
                  Confirmer
                </label>
                <input
                  data-testid="reset-confirm-input"
                  required
                  type="password"
                  minLength={6}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-2 w-full bg-white border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-3.5 text-[#2C2A29] transition-colors"
                  placeholder="••••••••"
                />
              </div>
              <button
                data-testid="reset-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-50 text-white rounded-full px-8 py-4 transition-colors"
              >
                {loading ? "Enregistrement…" : "Réinitialiser mon mot de passe"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
